/**
 * Chatbot Store
 *
 * Pinia store for chatbot widget state and conversation management.
 * Supports SSE streaming responses and conversation persistence.
 */
import { defineStore } from "pinia";
import { ref, computed, shallowRef } from "vue";
import { getDefaultQuickActions, defaultChatbotConfig, emptyConversation, } from "../models";
import { getCurrentAppLocale, translateApp } from "@/i18n";
import { messageQueueService } from "../services/message-queue.service";
import { conversationService } from "../services/conversation.service";
import { chatRepository } from "../repositories/chat.repository";
const FALLBACK_HISTORY_LIMIT = 16;
// =============================================================================
// STORE
// =============================================================================
export const useChatbotStore = defineStore("chatbot", () => {
    // ---------------------------------------------------------------------------
    // STATE
    // ---------------------------------------------------------------------------
    const widgetState = ref("closed");
    const conversation = ref({
        ...emptyConversation,
        messages: [],
    });
    const config = ref({ ...defaultChatbotConfig });
    const isInitialized = ref(false);
    const streamingMessageId = ref(null);
    const activeStreamController = shallowRef(null);
    let streamSequence = 0;
    // ---------------------------------------------------------------------------
    // GETTERS
    // ---------------------------------------------------------------------------
    const isOpen = computed(() => widgetState.value === "open");
    const isMinimized = computed(() => widgetState.value === "minimized");
    const messages = computed(() => conversation.value.messages);
    const isTyping = computed(() => conversation.value.isTyping);
    const hasMessages = computed(() => conversation.value.messages.length > 0);
    const conversationId = computed(() => conversation.value.conversationId);
    const isStreaming = computed(() => streamingMessageId.value !== null);
    const shouldShowWelcome = computed(() => {
        const userMessages = conversation.value.messages.filter((m) => m.role === "user");
        return userMessages.length === 0;
    });
    const shouldShowQuickActions = computed(() => shouldShowWelcome.value);
    const quickActions = computed(() => {
        getCurrentAppLocale();
        return getDefaultQuickActions();
    });
    // ---------------------------------------------------------------------------
    // ACTIONS
    // ---------------------------------------------------------------------------
    function openWidget() {
        widgetState.value = "open";
        if (!isInitialized.value) {
            initialize();
        }
    }
    function closeWidget() {
        widgetState.value = "closed";
    }
    function minimizeWidget() {
        widgetState.value = "minimized";
    }
    function toggleWidget() {
        if (isOpen.value) {
            minimizeWidget();
        }
        else {
            openWidget();
        }
    }
    function initialize() {
        if (isInitialized.value)
            return;
        const welcomeMessage = messageQueueService.createAIMessage(translateApp("chatbot.welcome"));
        conversation.value = conversationService.addMessage(conversation.value, welcomeMessage);
        isInitialized.value = true;
    }
    function abortActiveStream() {
        streamSequence += 1;
        activeStreamController.value?.abort();
        activeStreamController.value = null;
        if (streamingMessageId.value) {
            const streamId = streamingMessageId.value;
            conversation.value = {
                ...conversation.value,
                messages: conversation.value.messages.filter((m) => m.id !== streamId),
                isTyping: false,
            };
        }
        streamingMessageId.value = null;
    }
    function isAbortError(error) {
        if (error instanceof DOMException && error.name === "AbortError") {
            return true;
        }
        return (typeof error === "object" &&
            error !== null &&
            ("name" in error || "code" in error) &&
            (error.name === "CanceledError" ||
                error.code === "ERR_CANCELED"));
    }
    function buildFallbackHistory() {
        const firstUserIndex = conversation.value.messages.findIndex((message) => message.role === "user");
        if (firstUserIndex === -1)
            return [];
        return conversation.value.messages
            .slice(firstUserIndex)
            .filter((message) => (message.role === "user" || message.role === "assistant") &&
            message.status !== "failed" &&
            message.content.trim().length > 0)
            .slice(-FALLBACK_HISTORY_LIMIT)
            .map((message) => ({
            id: message.id,
            role: message.role,
            content: message.content.trim().slice(0, 5000),
        }));
    }
    async function sendMessage(content) {
        const trimmed = content.trim();
        if (!trimmed)
            return;
        abortActiveStream();
        const fallbackHistory = buildFallbackHistory();
        let streamController = null;
        let currentStreamSequence = streamSequence;
        let currentStreamingMessageId = null;
        const isCurrentStream = () => streamController !== null &&
            activeStreamController.value === streamController &&
            streamSequence === currentStreamSequence &&
            streamingMessageId.value === currentStreamingMessageId;
        // 1. Create optimistic message
        const optimistic = messageQueueService.createOptimisticMessage(trimmed);
        conversation.value = conversationService.addMessage(conversation.value, optimistic);
        // 2. Update status to 'sent' after delay
        setTimeout(() => {
            conversation.value = conversationService.updateMessage(conversation.value, optimistic.id, { status: "sent" });
        }, config.value.sentStatusDelay);
        // 3. Set typing indicator
        conversation.value = conversationService.setTyping(conversation.value, true);
        try {
            // 4. Create streaming placeholder message
            const streamingMsg = messageQueueService.createStreamingMessage();
            conversation.value = conversationService.addMessage(conversation.value, streamingMsg);
            streamingMessageId.value = streamingMsg.id;
            currentStreamingMessageId = streamingMsg.id;
            // 5. Stop typing indicator (streaming message replaces it)
            conversation.value = conversationService.setTyping(conversation.value, false);
            streamController = new AbortController();
            activeStreamController.value = streamController;
            currentStreamSequence = streamSequence;
            // 6. Stream response via SSE
            const result = await chatRepository.streamMessage({
                message: trimmed,
                locale: getCurrentAppLocale() === "en" ? "en" : "es",
                conversationId: conversation.value.conversationId ?? undefined,
                ...(fallbackHistory.length > 0
                    ? { history: fallbackHistory }
                    : {}),
            }, {
                onTextDelta(text) {
                    if (!isCurrentStream())
                        return;
                    conversation.value = conversationService.updateMessage(conversation.value, streamingMsg.id, {
                        content: (conversation.value.messages.find((m) => m.id === streamingMsg.id)?.content ?? "") + text,
                    });
                },
            }, { signal: streamController.signal });
            if (!isCurrentStream())
                return;
            activeStreamController.value = null;
            streamingMessageId.value = null;
            // 7. Store conversation ID
            if (result.conversationId) {
                conversation.value = conversationService.setConversationId(conversation.value, result.conversationId);
            }
            // 8. Ensure final text is set (in case deltas were partial)
            if (result.fullText) {
                conversation.value = conversationService.updateMessage(conversation.value, streamingMsg.id, { content: result.fullText });
            }
            else {
                conversation.value = conversationService.updateMessage(conversation.value, streamingMsg.id, { content: translateApp("chatbot.auto.empty_response") });
            }
            // 9. If draft received, add a draft message
            if (result.draft) {
                handleDraft(result.draft);
            }
        }
        catch (error) {
            const wasAborted = isAbortError(error);
            if (wasAborted || !isCurrentStream()) {
                return;
            }
            conversation.value = conversationService.setTyping(conversation.value, false);
            // Remove the streaming placeholder if it exists
            if (streamingMessageId.value) {
                const streamId = streamingMessageId.value;
                conversation.value = {
                    ...conversation.value,
                    messages: conversation.value.messages.filter((m) => m.id !== streamId),
                };
                streamingMessageId.value = null;
            }
            activeStreamController.value = null;
            conversation.value = conversationService.updateMessage(conversation.value, optimistic.id, { status: "failed" });
            const errorMessage = messageQueueService.createAIMessage(translateApp("chatbot.auto.processing_error"));
            conversation.value = conversationService.addMessage(conversation.value, errorMessage);
        }
    }
    function handleDraft(draft) {
        const draftMessage = messageQueueService.createDraftMessage(translateApp("chatbot.auto.draft_ready"), draft);
        conversation.value = conversationService.addMessage(conversation.value, draftMessage);
    }
    async function sendQuickAction(action) {
        await sendMessage(action.prompt);
    }
    function clearConversation() {
        abortActiveStream();
        conversation.value = conversationService.clear();
        isInitialized.value = false;
    }
    function $reset() {
        abortActiveStream();
        widgetState.value = "closed";
        conversation.value = { ...emptyConversation, messages: [] };
        isInitialized.value = false;
    }
    // ---------------------------------------------------------------------------
    // RETURN
    // ---------------------------------------------------------------------------
    return {
        // State
        widgetState,
        conversation,
        config,
        quickActions,
        isInitialized,
        streamingMessageId,
        // Getters
        isOpen,
        isMinimized,
        messages,
        isTyping,
        hasMessages,
        conversationId,
        isStreaming,
        shouldShowWelcome,
        shouldShowQuickActions,
        // Actions
        openWidget,
        closeWidget,
        minimizeWidget,
        toggleWidget,
        initialize,
        sendMessage,
        sendQuickAction,
        clearConversation,
        $reset,
    };
});
